import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  MapPin,
  Mail,
  DollarSign,
  FileText,
  CheckCircle,
  Star,
  PenLine,
  Image as ImageIcon,
  Loader2,
  AlertTriangle,
  Phone,
  Shield,
  Users,
} from 'lucide-react';
import { HelpIcon } from '../HelpIcon';
import { getClientNoteSections, type ClientNoteSection } from '@digitaltattoo/shared-utils';
import { DocumentSignModal } from '../DocumentSignModal';
import { DocumentPreviewModal } from '../DocumentPreviewModal';
import { AppointmentActsTraceabilitySection } from '../AppointmentActsTraceabilitySection';
import { summarizeAdminAppointmentPricing } from '../../utils/adminAppointmentPricing';
import type {
  AppointmentConsultationData,
  AppointmentReferenceImage,
  ConsentByActId,
  ConsultationAct,
} from './appointmentConsultationTypes';
import {
  formatConsultationDate,
  formatConsultationTime,
  parseContractSignatures,
  parseIdCardPhotosList,
  STATUS_BADGE_CONFIG,
  TYPE_BADGE_CONFIG,
} from './appointmentConsultationUtils';
import { ReferenceImageViewerModal } from './ReferenceImageViewerModal';

export interface AppointmentConsultationViewProps {
  appointment: AppointmentConsultationData;
  acts: ConsultationAct[];
  actsLoading?: boolean;
  consentByActId: ConsentByActId;
  docs: any[];
  docsLoading?: boolean;
  listPath: string;
  showHelpIcon?: boolean;
  allowDocumentSign?: boolean;
  allowDocumentView?: boolean;
  onOpenConsentPdf: (actId: string) => Promise<Blob | null>;
  signerName: string;
  onReloadDocs: () => void;
  ToastComponent?: ReactNode;
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.SCHEDULED;
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}
    >
      <Icon className="w-4 h-4 mr-1" />
      {config.label}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const config = TYPE_BADGE_CONFIG[type] || TYPE_BADGE_CONFIG.CONSULTATION;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

export function AppointmentConsultationView({
  appointment,
  acts,
  actsLoading = false,
  consentByActId,
  docs,
  docsLoading = false,
  listPath,
  showHelpIcon = false,
  allowDocumentSign = true,
  allowDocumentView = true,
  onOpenConsentPdf,
  signerName,
  onReloadDocs,
  ToastComponent,
}: AppointmentConsultationViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [signDoc, setSignDoc] = useState<any | null>(null);
  const [viewerImage, setViewerImage] = useState<AppointmentReferenceImage | null>(null);
  const [generatingConsentPdfActId, setGeneratingConsentPdfActId] = useState<string | null>(null);
  const [consentPdfPreview, setConsentPdfPreview] = useState<{
    blobUrl: string;
    fileName: string;
    title: string;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (consentPdfPreview?.blobUrl) {
        URL.revokeObjectURL(consentPdfPreview.blobUrl);
      }
    };
  }, [consentPdfPreview]);

  const closeConsentPdfPreview = () => setConsentPdfPreview(null);

  const downloadConsentPdf = () => {
    if (!consentPdfPreview) return;

    const link = document.createElement('a');
    link.href = consentPdfPreview.blobUrl;
    link.download = consentPdfPreview.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenConsentPdf = async (actId: string) => {
    if (generatingConsentPdfActId) return;

    setGeneratingConsentPdfActId(actId);
    try {
      const pdf = await onOpenConsentPdf(actId);
      if (!pdf) return;

      const act = acts.find((item) => item.id === actId);
      setConsentPdfPreview({
        blobUrl: URL.createObjectURL(pdf),
        fileName: `consentement-piercing-${act?.sequence ?? actId}.pdf`,
        title: `Consentement piercing — acte ${act?.sequence ?? '?'}`,
      });
    } finally {
      setGeneratingConsentPdfActId(null);
    }
  };

  const clientNoteSections = getClientNoteSections(appointment.clientNotes);

  const pricingSummary = useMemo(
    () =>
      summarizeAdminAppointmentPricing(
        (appointment.pricing ?? null) as unknown as Record<string, unknown> | null,
        {
          ...(acts.length > 0 ? { acts: acts as any } : {}),
          appointmentType: appointment.type,
          recordedDepositAmount: appointment.depositAmount,
        },
      ),
    [appointment.pricing, appointment.type, appointment.depositAmount, acts],
  );

  const expectedDeposit = pricingSummary.deposit;
  const isDepositPaid = appointment.depositStatus === 'PAID';
  const isDepositRequired = Boolean(appointment.depositRequired || expectedDeposit > 0);
  const depositLabel = !isDepositRequired
    ? 'Acompte'
    : isDepositPaid
      ? 'Acompte versé'
      : 'Acompte à verser';
  const depositValue = !isDepositRequired ? 0 : expectedDeposit;

  const hasPreparation =
    appointment.preparation &&
    ((appointment.preparation.beforeAppointment?.length ?? 0) > 0 ||
      (appointment.preparation.whatToBring?.length ?? 0) > 0 ||
      (appointment.preparation.restrictions?.length ?? 0) > 0);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {ToastComponent}
      <ReferenceImageViewerModal
        image={viewerImage}
        onClose={() => setViewerImage(null)}
      />
      <DocumentPreviewModal
        isOpen={consentPdfPreview !== null}
        title={consentPdfPreview?.title ?? 'Consentement piercing'}
        blobUrl={consentPdfPreview?.blobUrl}
        mimeType="application/pdf"
        onClose={closeConsentPdfPreview}
        onDownload={downloadConsentPdf}
      />

      {/* Header — back + title + check-in; no Modifier/Supprimer */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(listPath)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>

          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Rendez-vous #{appointment.id.slice(-8)}</span>
              {showHelpIcon && (
                <HelpIcon
                  docPath="07-gestion-rendez-vous#07-4-consultation"
                  tooltip="Documentation : Consultation d'un rendez-vous"
                />
              )}
            </h1>
            <p className="text-textSecondary">
              {appointment.clientFirstName} {appointment.clientLastName} - {appointment.serviceName}
            </p>
            {appointment.checkInCode && (
              <p className="mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm">
                <span className="text-xs uppercase tracking-wider text-gray-500">Code d’accueil</span>
                <span className="font-bold tracking-[0.3em] text-textPrimary">
                  {appointment.checkInCode}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client + practitioner + compact appointment details */}
<div className="card">

  <div className="flex items-center space-x-3 mb-4">
    <div className="flex items-center justify-center w-9 h-9 bg-primary-100 rounded-lg">
      <Calendar className="w-4 h-4 text-primary-600" />
    </div>

    <h2 className="text-lg font-semibold text-textPrimary">
      Rendez-vous
    </h2>
  </div>


  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">

    <div
      className={`rounded-lg border px-3 py-3 ${
        appointment.clientIsMinor
          ? 'border-amber-200 bg-amber-50'
          : 'border-blue-100 bg-blue-50/50'
      }`}
    >
      {/* Titre */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <User
            className={`w-3.5 h-3.5 ${
              appointment.clientIsMinor
                ? 'text-amber-700'
                : 'text-blue-600'
            }`}
          />

          <p
            className={`text-xs font-semibold uppercase tracking-wide ${
              appointment.clientIsMinor
                ? 'text-amber-800'
                : 'text-blue-800'
            }`}
          >
            Client
          </p>
        </div>

        {/* Badge mineur */}
        {appointment.clientIsMinor && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
            <Shield className="w-3 h-3" />
            Mineur
          </span>
        )}
      </div>

      {/* Nom */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-textPrimary text-sm">
          {appointment.clientFirstName}{' '}
          {appointment.clientLastName}
        </p>
      </div>

      {/* Email client */}
      {appointment.clientEmail && (
        <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5 min-w-0">
          <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />

          <span className="truncate">
            {appointment.clientEmail}
          </span>
        </p>
      )}

      {/* ================================================= */}
      {/* TUTEUR LÉGAL */}
      {/* ================================================= */}

      {appointment.clientIsMinor && (
        <div className="mt-3 border-t border-amber-200 pt-3">
          {appointment.legalGuardian ? (
            <>
              {/* Header tuteur */}
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-amber-700" />

                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  Tuteur légal
                </p>

                <span className="ml-auto inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                  Renseigné
                </span>
              </div>

              {/* Nom */}
              <p className="text-sm font-semibold text-gray-900">
                {appointment.legalGuardian.firstName}{' '}
                {appointment.legalGuardian.lastName}
              </p>

              {/* Relation */}
              {appointment.legalGuardian.relationshipToMinor && (
                <p className="mt-0.5 text-xs text-gray-600">
                  {appointment.legalGuardian.relationshipToMinor ===
                  'parent'
                    ? 'Parent'
                    : appointment.legalGuardian
                          .relationshipToMinor ===
                        'legal_guardian'
                      ? 'Tuteur légal'
                      : appointment.legalGuardian
                            .relationshipToMinor ===
                          'mother'
                        ? 'Mère'
                        : appointment.legalGuardian
                              .relationshipToMinor ===
                            'father'
                          ? 'Père'
                          : appointment.legalGuardian
                                .relationshipDetails ||
                              'Autre'}
                </p>
              )}

              {/* Coordonnées */}
              <div className="mt-2 space-y-1">
                {appointment.legalGuardian.phone && (
                  <a
                    href={`tel:${appointment.legalGuardian.phone}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-primary-600"
                  >
                    <Phone className="w-3.5 h-3.5 text-gray-400" />

                    {appointment.legalGuardian.phone}
                  </a>
                )}

                {appointment.legalGuardian.email && (
                  <a
                    href={`mailto:${appointment.legalGuardian.email}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-primary-600 min-w-0"
                  >
                    <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />

                    <span className="truncate">
                      {appointment.legalGuardian.email}
                    </span>
                  </a>
                )}
              </div>
            </>
          ) : (
            /* Mineur sans tuteur */
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5">
              <AlertTriangle className="w-4 h-4 shrink-0 text-red-600 mt-0.5" />

              <div>
                <p className="text-xs font-semibold text-red-800">
                  Aucun tuteur légal renseigné
                </p>

                <p className="mt-0.5 text-[11px] text-red-600">
                  Les informations du représentant légal sont
                  requises pour ce client mineur.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    {/* =================================================== */}
    {/* PRATICIEN */}
    {/* =================================================== */}

    <div className="rounded-lg border border-purple-100 bg-purple-50/50 px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <Star className="w-3.5 h-3.5 text-purple-600" />

        <p className="text-xs font-semibold uppercase tracking-wide text-purple-800">
          {t('navigation.practitioners.title')}
        </p>
      </div>

      {appointment.practitionerFirstName &&
      appointment.practitionerLastName ? (
        <>
          <p className="font-medium text-textPrimary text-sm">
            {appointment.practitionerFirstName}{' '}
            {appointment.practitionerLastName}
          </p>

          <p className="mt-1.5">
            {appointment.takenInChargeAt ? (
              <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-800">
                Prise en charge ·{' '}
                {formatConsultationDate(
                  String(appointment.takenInChargeAt),
                )}{' '}
                {formatConsultationTime(
                  String(appointment.takenInChargeAt),
                )}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                Assigné à l’avance
              </span>
            )}
          </p>
        </>
      ) : (
        <p className="font-medium text-textPrimary text-sm">
          Non assigné
        </p>
      )}

      {(appointment.reservedPractitionerFirstName ||
        appointment.reservedPractitionerLastName) && (
        <p className="text-xs text-gray-600 mt-2">
          Créneau réservé :{' '}
          <span className="font-medium text-gray-800">
            {[
              appointment.reservedPractitionerFirstName,
              appointment.reservedPractitionerLastName,
            ]
              .filter(Boolean)
              .join(' ')}
          </span>

          {appointment.reservedPractitionerId &&
          appointment.practitionerId &&
          appointment.reservedPractitionerId !==
            appointment.practitionerId
            ? ' (différent du praticien en charge)'
            : ''}
        </p>
      )}
    </div>
  </div>

  {/* ===================================================== */}
  {/* INFORMATIONS DU RENDEZ-VOUS */}
  {/* ===================================================== */}

  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm border-t border-gray-100 pt-4">
    {/* Date */}
    <div>
      <p className="text-xs text-gray-500 mb-0.5">
        Date
      </p>

      <p className="font-medium">
        {formatConsultationDate(
          appointment.scheduledAt,
        )}
      </p>
    </div>

    {/* Heure */}
    <div>
      <p className="text-xs text-gray-500 mb-0.5">
        Heure
      </p>

      <p className="font-medium">
        {formatConsultationTime(
          appointment.scheduledAt,
        )}
        {' – '}
        {formatConsultationTime(
          appointment.estimatedEndTime,
        )}
      </p>
    </div>

    {/* Service */}
    <div>
      <p className="text-xs text-gray-500 mb-0.5">
        Service
      </p>

      <p className="font-medium leading-snug">
        {appointment.serviceName}
      </p>

      <div className="mt-1">
        <TypeBadge type={appointment.type} />
      </div>
    </div>

    {/* Shop */}
    <div>
      <p className="text-xs text-gray-500 mb-0.5">
        Shop
      </p>

      <p className="font-medium flex items-start gap-1">
        <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />

        <span>{appointment.shopName}</span>
      </p>
    </div>
  </div>

  {/* ===================================================== */}
  {/* NOTES DU RENDEZ-VOUS */}
  {/* ===================================================== */}

  {appointment.notes && (
    <div className="mt-4">
      <h3 className="text-xs font-medium text-gray-500 mb-1.5">
        Notes
      </h3>

      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
        {appointment.notes}
      </p>
    </div>
  )}

  {/* ===================================================== */}
  {/* PROJET TATOUAGE */}
  {/* ===================================================== */}

  {appointment.project && (
    <div className="mt-4">
      <h3 className="text-xs font-medium text-gray-500 mb-1.5">
        Projet tatouage
      </h3>

      <div className="bg-gray-50 px-3 py-2 rounded-lg space-y-1.5 text-sm">
        {appointment.project.description && (
          <p className="text-gray-900">
            {appointment.project.description}
          </p>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
          {appointment.project.style && (
            <p>
              <span className="font-medium text-gray-700">
                Style :
              </span>{' '}
              {appointment.project.style}
            </p>
          )}

          {appointment.project.placement && (
            <p>
              <span className="font-medium text-gray-700">
                Emplacement :
              </span>{' '}
              {appointment.project.placement}
            </p>
          )}

          {appointment.project.size && (
            <p>
              <span className="font-medium text-gray-700">
                Taille :
              </span>{' '}
              {appointment.project.size}
            </p>
          )}

          {appointment.project.colorType && (
            <p>
              <span className="font-medium text-gray-700">
                Couleur :
              </span>{' '}
              {appointment.project.colorType}
            </p>
          )}
        </div>
      </div>
    </div>
  )}
  {appointment.referenceImages &&
    appointment.referenceImages.length > 0 && (
      <div className="mt-4">
        <h3 className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5" />
          Images de référence
        </h3>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {appointment.referenceImages.map(
            (img) => (
              <button
                key={img.id}
                type="button"
                onClick={() =>
                  setViewerImage(img)
                }
                className="block group rounded-lg overflow-hidden border border-gray-200 hover:border-violet-400 transition-colors bg-white text-left"
              >
                <img
                  src={
                    img.thumbnailUrl ||
                    img.url
                  }
                  alt={img.fileName}
                  className="w-full h-28 object-cover group-hover:opacity-90 transition-opacity"
                />

                <p className="text-xs text-gray-500 truncate px-2 py-1">
                  {img.fileName}
                </p>
              </button>
            ),
          )}
        </div>
      </div>
    )}

  {/* ===================================================== */}
  {/* NOTES CLIENT */}
  {/* ===================================================== */}

  {clientNoteSections.length > 0 && (
    <div className="mt-3">
      <h3 className="text-xs font-medium text-gray-500 mb-1.5">
        Notes du client
      </h3>

      <div className="space-y-2">
        {clientNoteSections.map(
          (
            section: ClientNoteSection,
            index: number,
          ) => (
            <div
              key={`${
                section.title || 'note'
              }-${index}`}
              className="bg-blue-50 px-3 py-2 rounded-lg"
            >
              {section.title && (
                <p className="text-xs font-medium text-blue-900 mb-1">
                  {section.title}
                </p>
              )}

              <div
                className="
                  text-gray-900 text-sm max-w-none
                  [&_ul]:list-disc
                  [&_ul]:pl-6
                  [&_ul]:my-2
                  [&_ol]:list-decimal
                  [&_ol]:pl-6
                  [&_ol]:my-2
                  [&_li]:my-1
                  [&_p]:my-1
                "
                dangerouslySetInnerHTML={{
                  __html: section.html,
                }}
              />
            </div>
          ),
        )}
      </div>
    </div>
  )}
</div>

          {/* Acts & traceability — always shown, read-only (no Ajouter/Modifier) */}
          <AppointmentActsTraceabilitySection
            acts={acts.map((act) => ({
              id: act.id,
              sequence: act.sequence,
              status: act.status,
              serviceName: act.serviceName || act.serviceId || '—',
              notes: act.notes,
              performedAt: act.performedAt,
              demandDetails: act.demandDetails,
              piercingRecord: act.piercingRecord as any,
            }))}
            loading={actsLoading}
            className=""
          />

          {/* Piercing consent */}
          {Object.keys(consentByActId).length > 0 && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-9 h-9 bg-violet-100 rounded-lg">
                  <PenLine className="w-4 h-4 text-violet-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-textPrimary">Consentements piercing</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Contrats signés électroniquement (hors modèles Word générés ci-dessous).
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                {acts
                  .filter((a) => consentByActId[a.id])
                  .slice()
                  .sort(
                    (a, b) =>
                      (a.sequence ?? Number.MAX_SAFE_INTEGER) -
                      (b.sequence ?? Number.MAX_SAFE_INTEGER),
                  )
                  .map((act) => {
                    const contract = consentByActId[act.id];
                    const parsed = parseContractSignatures(contract.signatures);
                    const lg = contract.legalGuardian as { dataUrl?: string } | null;
                    const sigs: Record<string, { dataUrl?: string }> = { ...parsed };
                    if (lg?.dataUrl && !sigs.legalGuardian?.dataUrl) {
                      sigs.legalGuardian = { dataUrl: lg.dataUrl };
                    }
                    const idPhotos = parseIdCardPhotosList(contract.idCardPhotos);
                    const signerRows: { key: string; label: string; ok: boolean }[] = [
                      { key: 'practitioner', label: 'Praticien', ok: !!sigs.practitioner?.dataUrl },
                      { key: 'client', label: 'Client', ok: !!sigs.client?.dataUrl },
                      {
                        key: 'legalGuardian',
                        label: 'Représentant légal',
                        ok: !!sigs.legalGuardian?.dataUrl,
                      },
                    ];
                    const signedCount = signerRows.filter((r) => r.ok).length;
                    const canOpenPdf = contract.status === 'SIGNED' || signedCount > 0;
                    return (
                      <div
                        key={act.id}
                        className="rounded-lg border border-violet-200 bg-violet-50/40 p-4 space-y-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Acte {act.sequence ?? '?'} — {act.serviceName || act.serviceId || '—'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Statut contrat : {contract.status}
                              {contract.signedAt
                                ? ` · Signé le ${new Date(contract.signedAt).toLocaleString('fr-FR')}`
                                : ''}
                            </p>
                          </div>
                          {canOpenPdf && (
                            <button
                              type="button"
                              onClick={() => void handleOpenConsentPdf(act.id)}
                              disabled={generatingConsentPdfActId !== null}
                              aria-busy={generatingConsentPdfActId === act.id}
                              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:cursor-wait disabled:opacity-70"
                            >
                              {generatingConsentPdfActId === act.id && (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              )}
                              {generatingConsentPdfActId === act.id
                                ? 'Génération du PDF...'
                                : 'Voir le PDF'}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {signerRows.map((row) => (
                            <span
                              key={row.key}
                              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                                row.ok ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {row.ok ? (
                                <CheckCircle className="w-3 h-3" />
                              ) : (
                                <PenLine className="w-3 h-3" />
                              )}
                              {row.label}
                            </span>
                          ))}
                        </div>
                        {idPhotos.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">
                              Pièces d&apos;identité
                            </p>
                            <div className="flex flex-wrap gap-3">
                              {idPhotos.map((ph, i) => (
                                <div key={i} className="text-center">
                                  <div className="text-[10px] uppercase text-gray-500 mb-1">
                                    {ph.role === 'GUARDIAN' ? 'Représentant légal' : 'Client'}
                                    {' · '}
                                    {ph.side === 'back' ? 'Verso' : 'Recto'}
                                  </div>
                                  <a
                                    href={ph.dataUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block border rounded-lg overflow-hidden bg-white w-28 h-20"
                                  >
                                    <img
                                      src={ph.dataUrl}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Generated Documents */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-9 h-9 bg-indigo-100 rounded-lg">
                <FileText className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-textPrimary">Documents générés</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Modèles Word/PDF administratifs (confirmation de réservation, etc.) — distincts des
                  contrats de consentement piercing ci-dessus.
                </p>
              </div>
            </div>

            {docsLoading ? (
              <p className="text-sm text-gray-500">Chargement...</p>
            ) : docs.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-1.5">
                <p className="text-sm text-gray-700">
                  Aucun document administratif généré à partir des modèles pour ce rendez-vous.
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Les contrats de consentement piercing (PDF signés) sont listés dans « Consentements
                  piercing ». Cette section affiche uniquement les PDF/DOCX issus des modèles
                  document (ex. confirmation de réservation) liés au rendez-vous ou aux contrats
                  via le moteur de modèles Word.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {docs.map((doc) => {
                  const required: string[] = doc.requiredSigners ?? ['CLIENT'];
                  const signed: string[] = doc.signedRoles ?? [];
                  const pending = required.filter((r: string) => !signed.includes(r));
                  const roleLabels: Record<string, string> = {
                    CLIENT: 'Client',
                    LEGAL_GUARDIAN: 'Représentant légal',
                    PRACTITIONER: 'Praticien',
                    SHOP_MANAGER: 'Resp. shop',
                  };
                  return (
                    <div
                      key={doc.id}
                      className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {doc.templateCode}{' '}
                          <span className="text-gray-400 font-normal">v{doc.templateVersion}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Créé le {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {required.map((role: string) => (
                            <span
                              key={role}
                              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                                signed.includes(role)
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {signed.includes(role) ? (
                                <CheckCircle className="w-3 h-3" />
                              ) : (
                                <PenLine className="w-3 h-3" />
                              )}
                              {roleLabels[role] ?? role}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {allowDocumentView && (
                          <button
                            onClick={() => navigate(`/documents/view/${doc.id}`)}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Voir
                          </button>
                        )}
                        {allowDocumentSign && pending.length > 0 && (
                          <button
                            onClick={() => setSignDoc(doc)}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                          >
                            <PenLine className="w-3.5 h-3.5" />
                            Signer
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Preparation Instructions */}
          {hasPreparation && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-9 h-9 bg-yellow-100 rounded-lg">
                  <FileText className="w-4 h-4 text-yellow-600" />
                </div>
                <h2 className="text-lg font-semibold text-textPrimary">
                  Instructions de Préparation
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {appointment.preparation.beforeAppointment?.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Avant le rendez-vous</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {appointment.preparation.beforeAppointment.map((instruction, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-accent-500 mr-2">•</span>
                          {instruction}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {appointment.preparation.whatToBring?.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">À apporter</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {appointment.preparation.whatToBring.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-accent-500 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {appointment.preparation.restrictions?.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Restrictions</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {appointment.preparation.restrictions.map((restriction, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-500 mr-2">•</span>
                          {restriction}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Status & Pricing */}
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-9 h-9 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-textPrimary">Statut</h2>
            </div>

            <div className="text-center">
              <StatusBadge status={appointment.status} />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-9 h-9 bg-green-100 rounded-lg">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-textPrimary">Tarification</h2>
            </div>

            <div className="space-y-4">
              {pricingSummary.lines.length > 0 ? (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Détail</h4>
                  <ul className="space-y-2">
                    {pricingSummary.lines.map((line, idx) => (
                      <li key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 pr-2">{line.label}</span>
                        <span className="font-medium tabular-nums shrink-0">
                          {line.amount.toFixed(2)} €
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aucune ligne de tarification enregistrée.</p>
              )}

              <div className="border-t pt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Sous-total (lignes)</span>
                  <span className="font-medium tabular-nums">
                    {pricingSummary.computedSubtotal.toFixed(2)} €
                  </span>
                </div>
                {pricingSummary.storedSubtotal > 0 &&
                  Math.abs(pricingSummary.storedSubtotal - pricingSummary.computedSubtotal) >
                    0.02 && (
                    <p className="text-xs text-gray-400">
                      Sous-total issu de la réservation (JSON) :{' '}
                      {pricingSummary.storedSubtotal.toFixed(2)} €
                    </p>
                  )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{depositLabel}</span>
                  <span
                    className={`font-medium tabular-nums ${isDepositPaid ? 'text-green-600' : 'text-orange-600'}`}
                  >
                    {depositValue.toFixed(2)} €
                  </span>
                </div>

                <div className="flex items-center justify-between text-lg font-bold border-t pt-2 mt-2">
                  <span>Reste / total affiché</span>
                  <span className="text-accent-600 tabular-nums">
                    {pricingSummary.displayTotal.toFixed(2)} €
                  </span>
                </div>
                {pricingSummary.storedTotal > 0 &&
                  Math.abs(pricingSummary.storedTotal - pricingSummary.computedSubtotal) > 0.02 && (
                    <p className="text-xs text-amber-700">
                      Total stocké à la réservation : {pricingSummary.storedTotal.toFixed(2)} € — le
                      détail ci-dessus inclut les prestations et produits saisis en parcours
                      praticien.
                    </p>
                  )}
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Méthode de paiement</span>
                  <span className="font-medium capitalize">{pricingSummary.paymentMethod}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600">Devise</span>
                  <span className="font-medium">{pricingSummary.currency}</span>
                </div>
              </div>
            </div>
          </div>

          {appointment.reminders && appointment.reminders.enabled && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-9 h-9 bg-blue-100 rounded-lg">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-textPrimary">Rappels</h2>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Méthodes</span>
                  <span className="font-medium">{appointment.reminders.methods.join(', ')}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Jours avant</span>
                  <span className="font-medium">{appointment.reminders.daysBefore.join(', ')}</span>
                </div>

                {appointment.reminders.customMessage && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Message personnalisé</h4>
                    <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                      {appointment.reminders.customMessage}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Informations</h2>

            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Créé le</span>
                <span className="font-medium">{formatConsultationDate(appointment.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Dernière modification</span>
                <span className="font-medium">{formatConsultationDate(appointment.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">ID</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                  {appointment.id}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {signDoc &&
        (() => {
          const required: string[] = signDoc.requiredSigners ?? ['CLIENT'];
          const signed: string[] = signDoc.signedRoles ?? [];
          const pendingRoles = required.filter((r: string) => !signed.includes(r)) as (
            | 'CLIENT'
            | 'LEGAL_GUARDIAN'
            | 'PRACTITIONER'
            | 'SHOP_MANAGER'
          )[];
          return (
            <DocumentSignModal
              documentId={signDoc.id}
              documentName={`${signDoc.templateCode} v${signDoc.templateVersion}`}
              pdfUrl={`/api/documents/${signDoc.id}/pdf`}
              defaultSignerName={signerName}
              availableRoles={
                pendingRoles.length > 0
                  ? pendingRoles
                  : ['CLIENT', 'LEGAL_GUARDIAN', 'PRACTITIONER', 'SHOP_MANAGER']
              }
              requiredSigners={required}
              signedRoles={signed}
              authTokenKey="token"
              signEndpoint={`/api/documents/${signDoc.id}/sign`}
              onSuccess={() => {
                setSignDoc(null);
                onReloadDocs();
              }}
              onClose={() => setSignDoc(null)}
            />
          );
        })()}
    </div>
  );
}
